import { type FC, useContext, useMemo, useRef } from 'react';
import { Divider, Flex } from 'antd';
import { type NodeProps, useNodes } from '@xyflow/react';
import { Bleed, TextEllipsis } from '../../../../../../common/components';
import { FormField, FormSelect } from '../../../../../../common/form';
import { AlgorithmGraphReadOnlyField } from '../../../../AlgorithmGraphReadOnlyField';
import { GraphCard } from '../../../../GraphCard';
import { GraphCardTitle } from '../../../../GraphCardTitle';
import { getAlgorithmSourceLabel } from '../../../../utils';
import type { AlgorithmJoinsAndMappingsGraphNodeOfType } from '../../../schema/types';
import { AlgorithmJoinsWithMappingsGraphContext } from '../../AlgorithmJoinsWithMappingsGraphContext';
import { useNodeIndex } from '../../useNodeIndex';
import { AddAlgorithmSourceNodeItemButton } from './AddAlgorithmSourceNodeItemButton';
import { AddRemoveAlgorithmNodeButtons } from '../AddRemoveAlgorithmNodeButtons';
import { AlgorithmSourceNodeItem } from './AlgorithmSourceNodeItem';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmSourceNode.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-source-node',
);
addTranslations(translations);

export type AlgorithmSourceNodeProps = NodeProps<
  AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>
>;

export const AlgorithmSourceNode: FC<AlgorithmSourceNodeProps> = (props) => {
  const { id, data, selected } = props;
  const { algorithmSourceId, joinTypeCode, items } = data;
  const index = useNodeIndex(id);
  const sourceNodeIndex = useNodeIndex(id, 'algorithmSource');
  const isRoot = sourceNodeIndex === 0;
  const baseName = `nodes.${index}.data` as const;
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { readOnly, joinTypeOptions, algorithmSourcesById } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );

  const algorithmSource = algorithmSourcesById.get(algorithmSourceId);

  return (
    <GraphCard
      ref={cardRef}
      size="small"
      title={
        <GraphCardTitle
          tagLabel={t('algorithmSource')}
          tagColor="blue"
          isRoot={isRoot}
        >
          {!readOnly && (
            <AddRemoveAlgorithmNodeButtons nodeId={id} nodeIndex={index} />
          )}
        </GraphCardTitle>
      }
      className={selected ? 'selected' : undefined}
    >
      <Flex vertical align="stretch" gap="middle">
        {!isRoot &&
          (readOnly ? (
            <AlgorithmGraphReadOnlyField label={t('joinType')}>
              <TextEllipsis>
                {joinTypeOptions.find((o) => o.value === joinTypeCode)?.label}
              </TextEllipsis>
            </AlgorithmGraphReadOnlyField>
          ) : (
            <FormField
              name={`${baseName}.joinTypeCode`}
              required
              label={t('joinType')}
            >
              <FormSelect
                name={`${baseName}.joinTypeCode`}
                className="nodrag"
                options={joinTypeOptions}
              />
            </FormField>
          ))}
        {readOnly ? (
          <AlgorithmGraphReadOnlyField label={t('algorithmSource')}>
            <TextEllipsis>
              {getAlgorithmSourceLabel(algorithmSource!)}
            </TextEllipsis>
          </AlgorithmGraphReadOnlyField>
        ) : (
          <FormField
            name={`${baseName}.algorithmSourceId`}
            required
            label={t('algorithmSource')}
          >
            <AlgorithmSourceSelect
              name={`${baseName}.algorithmSourceId`}
              algorithmSourceId={algorithmSourceId}
            />
          </FormField>
        )}
        {algorithmSource && (
          <>
            <Bleed direction="horizontal" size="sm">
              <Divider style={{ margin: 0 }} />
            </Bleed>
            {items.map((item, itemIndex) => (
              <AlgorithmSourceNodeItem
                key={itemIndex}
                nodeId={id}
                algorithmSource={algorithmSource}
                item={item}
                itemIndex={itemIndex}
                baseName={baseName}
                isRoot={isRoot}
              />
            ))}
            {!readOnly && (
              <Flex justify="end">
                <AddAlgorithmSourceNodeItemButton
                  nodeId={id}
                  nodeIndex={index}
                />
              </Flex>
            )}
          </>
        )}
      </Flex>
    </GraphCard>
  );
};

interface AlgorithmSourceSelectProps {
  name: `nodes.${number}.data.algorithmSourceId`;
  algorithmSourceId: number;
}

const AlgorithmSourceSelect: FC<AlgorithmSourceSelectProps> = (props) => {
  const { name, algorithmSourceId } = props;

  const nodes =
    useNodes<AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>>();

  const { algorithmSourcesById } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );
  const allUsedAlgorithmSourceIds = useMemo(
    () =>
      nodes.reduce<number[]>(
        (algorithmSourceIds, { data }) =>
          'algorithmSourceId' in data
            ? algorithmSourceIds.concat(data.algorithmSourceId)
            : algorithmSourceIds,
        [],
      ),
    [nodes],
  );

  const sourceOptions = useMemo(
    () =>
      Array.from(algorithmSourcesById.values())
        .filter(
          (source) =>
            source.id === algorithmSourceId ||
            !allUsedAlgorithmSourceIds.includes(source.id),
        )
        .map((source) => ({
          value: source.id,
          label: getAlgorithmSourceLabel(source),
        })),
    [algorithmSourcesById, allUsedAlgorithmSourceIds, algorithmSourceId],
  );

  return <FormSelect name={name} className="nodrag" options={sourceOptions} />;
};
